import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { boothService } from '../../../services/boothService';
import { storageService } from '../../../services/storageService';
import { validateImageFile, getImageUploadNotice } from '../../../utils/imageValidation';
import { supabase } from '../../../lib/supabase';



const BoothContentManager = () => {
  const { user, userProfile } = useAuth();
  const [uploadProgress, setUploadProgress] = useState({});
  const [activeTab, setActiveTab] = useState('videos');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'url'
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [editingVideo, setEditingVideo] = useState(null);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [tourTitle, setTourTitle] = useState('');
  const [tourUrl, setTourUrl] = useState('');
  const [tourUploadType, setTourUploadType] = useState('url'); // 'file' or 'url' for campus tour
  const [tourImageFile, setTourImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instituteId, setInstituteId] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const [contentItems, setContentItems] = useState({
    videos: [],
    brochures: [],
    links: [],
    campusTours: []
  });

  const tabs = [
    { id: 'logo', label: 'Logo', icon: 'Image', count: logoUrl ? 1 : 0 },
    { id: 'videos', label: 'Videos', icon: 'Video', count: contentItems?.videos?.length },
    { id: 'brochures', label: 'Brochures', icon: 'FileText', count: contentItems?.brochures?.length },
    { id: 'links', label: 'Links', icon: 'Link', count: contentItems?.links?.length },
    { id: 'campusTours', label: '360 Campus Tour', icon: 'Compass', count: contentItems?.campusTours?.length }
  ];

  useEffect(() => {
    const fetchInstituteProfile = async () => {
      if (user?.id && userProfile?.institute_profiles?.[0]) {
        try {
          const profile = userProfile?.institute_profiles?.[0];
          setInstituteId(profile?.id);
          setLogoUrl(profile?.logo_url);
          
          // Load existing booth data
          const boothData = await boothService?.getBoothByInstituteId(profile?.id);

          // Load booth_resources (brochures/pdfs) from DB
          const { data: resources } = await supabase
            ?.from('booth_resources')
            ?.select('*')
            ?.eq('institute_id', profile?.user_id || user?.id)
            ?.order('created_at', { ascending: false });

          const dbBrochures = (resources || [])
            ?.filter(r => ['pdf', 'doc', 'ppt']?.includes(r?.resource_type))
            ?.map((r) => ({
              id: r?.id,
              title: r?.resource_name,
              type: r?.resource_type || 'pdf',
              size: r?.file_size ? `${(r?.file_size / (1024 * 1024))?.toFixed(1)} MB` : 'Unknown',
              uploadedDate: r?.created_at ? new Date(r?.created_at)?.toISOString()?.split('T')?.[0] : '',
              status: 'active',
              downloads: r?.download_count || 0,
              url: r?.file_url
            }));

          if (boothData) {
            // Load videos from virtual_booths.video_urls
            const dbVideos = (boothData?.video_urls || [])?.map((v, index) => ({
              id: v?.id || `video-${index}`,
              title: v?.title || `Video ${index + 1}`,
              type: 'video',
              size: v?.size || 'URL',
              uploadedDate: v?.uploadedDate || new Date()?.toISOString()?.split('T')?.[0],
              status: 'active',
              views: v?.views || 0,
              url: v?.url || v
            }));

            // Update links from database
            const dbLinks = boothData?.application_links || [];
            // Update campus tours from database
            const dbTours = boothData?.campus_tour_urls || [];

            setContentItems({
              videos: dbVideos,
              brochures: dbBrochures,
              links: dbLinks?.map((link, index) => ({
                id: index + 1,
                title: link?.title,
                url: link?.url,
                clicks: 0,
                status: 'active'
              })),
              campusTours: dbTours?.map((tour, index) => ({
                id: index + 1,
                title: tour?.title,
                url: tour?.url,
                uploadedDate: tour?.uploadedDate || new Date()?.toISOString()?.split('T')?.[0],
                status: 'active',
                views: 0
              }))
            });
          } else {
            // No booth yet, still load brochures from booth_resources
            setContentItems(prev => ({
              ...prev,
              brochures: dbBrochures
            }));
          }
        } catch (error) {
          console.error('Error fetching institute profile:', error);
        }
      }
    };
    
    fetchInstituteProfile();
  }, [user]);

  // Handle pre-selected upload type from dashboard
  useEffect(() => {
    const selectedType = sessionStorage.getItem('selectedUploadType');
    if (selectedType) {
      setActiveTab(selectedType);
      setShowUploadModal(true);
      sessionStorage.removeItem('selectedUploadType');
    }
  }, []);

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const fileId = `upload-${Date.now()}`;
    setUploadProgress({ ...uploadProgress, [fileId]: 0 });
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const currentProgress = prev?.[fileId] || 0;
        if (currentProgress >= 100) {
          clearInterval(interval);
          
          // Add the uploaded file to content items
          const newItem = {
            id: Date.now(),
            title: file?.name,
            type: activeTab === 'videos' ? 'video' : 'pdf',
            size: `${(file?.size / (1024 * 1024))?.toFixed(1)} MB`,
            uploadedDate: new Date()?.toISOString()?.split('T')?.[0],
            status: "active",
            ...(activeTab === 'videos' ? { views: 0 } : { downloads: 0 })
          };

          setContentItems(prev => ({
            ...prev,
            [activeTab]: [...prev?.[activeTab], newItem]
          }));

          setTimeout(() => {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress?.[fileId];
              return newProgress;
            });
            setShowUploadModal(false);
          }, 1000);
          return prev;
        }
        return { ...prev, [fileId]: currentProgress + 10 };
      });
    }, 200);
  };

  const handleVideoUrlSubmit = () => {
    if (!videoUrl?.trim()) return;

    if (editingVideo) {
      // Update existing video
      setContentItems(prev => ({
        ...prev,
        videos: prev?.videos?.map(video => 
          video?.id === editingVideo?.id 
            ? { ...video, title: videoTitle?.trim() || video?.title, url: videoUrl }
            : video
        )
      }));
      setEditingVideo(null);
    } else {
      // Add new video
      const newVideo = {
        id: Date.now(),
        title: videoTitle?.trim() || `Video - ${new Date()?.toLocaleString()}`,
        type: "video",
        size: "URL",
        uploadedDate: new Date()?.toISOString()?.split('T')?.[0],
        status: "active",
        views: 0,
        url: videoUrl
      };

      setContentItems(prev => ({
        ...prev,
        videos: [...prev?.videos, newVideo]
      }));
    }

    setVideoUrl('');
    setVideoTitle('');
    setShowUploadModal(false);
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoUrl(video?.url || '');
    setVideoTitle(video?.title || '');
    setUploadType('url');
    setShowUploadModal(true);
  };

  const handleLinkSubmit = async () => {
    if (!linkTitle?.trim() || !linkUrl?.trim()) return;

    if (!instituteId) {
      alert('Institute profile not found. Please try again.');
      return;
    }

    try {
      setLoading(true);
      
      // Save to database
      const linkData = {
        title: linkTitle,
        url: linkUrl
      };
      
      await boothService?.addApplicationLink(instituteId, linkData);

      // Update local state
      const newLink = {
        id: Date.now(),
        title: linkTitle,
        url: linkUrl,
        clicks: 0,
        status: "active"
      };

      setContentItems(prev => ({
        ...prev,
        links: [...prev?.links, newLink]
      }));

      setLinkTitle('');
      setLinkUrl('');
      setShowUploadModal(false);
      alert('Application link added successfully!');
    } catch (error) {
      console.error('Error adding link:', error);
      alert('Failed to add link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCampusTourSubmit = async () => {
    if (!tourTitle?.trim() || !tourUrl?.trim()) return;

    if (!instituteId) {
      alert('Institute profile not found. Please try again.');
      return;
    }

    try {
      setLoading(true);
      
      // Save to database
      const tourData = {
        title: tourTitle,
        url: tourUrl,
        uploadedDate: new Date()?.toISOString()?.split('T')?.[0]
      };
      
      await boothService?.addCampusTourUrl(instituteId, tourData);

      // Update local state
      const newTour = {
        id: Date.now(),
        title: tourTitle,
        url: tourUrl,
        uploadedDate: tourData?.uploadedDate,
        status: "active",
        views: 0
      };

      setContentItems(prev => ({
        ...prev,
        campusTours: [...prev?.campusTours, newTour]
      }));

      setTourTitle('');
      setTourUrl('');
      setShowUploadModal(false);
      alert('360 Campus Tour added successfully!');
    } catch (error) {
      console.error('Error adding campus tour:', error);
      alert('Failed to add campus tour. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (activeTab === 'links') {
      if (!instituteId) {
        alert('Institute profile not found.');
        return;
      }

      try {
        setLoading(true);
        
        // Find the index of the link to delete
        const linkIndex = contentItems?.links?.findIndex(item => item?.id === itemId);
        
        if (linkIndex !== -1) {
          // Delete from database
          await boothService?.removeApplicationLink(instituteId, linkIndex);
          
          // Update local state
          setContentItems(prev => ({
            ...prev,
            links: prev?.links?.filter(item => item?.id !== itemId)
          }));
          
          alert('Link deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting link:', error);
        alert('Failed to delete link. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (activeTab === 'campusTours') {
      if (!instituteId) {
        alert('Institute profile not found.');
        return;
      }

      try {
        setLoading(true);
        
        // Find the index of the tour to delete
        const tourIndex = contentItems?.campusTours?.findIndex(item => item?.id === itemId);
        
        if (tourIndex !== -1) {
          // Delete from database
          await boothService?.removeCampusTourUrl(instituteId, tourIndex);
          
          // Update local state
          setContentItems(prev => ({
            ...prev,
            campusTours: prev?.campusTours?.filter(item => item?.id !== itemId)
          }));
        }
        
        alert('Campus tour deleted successfully!');
      } catch (error) {
        console.error('Error deleting campus tour:', error);
        alert('Failed to delete campus tour. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // For videos and brochures
      if (activeTab === 'videos') {
        try {
          setLoading(true);

          // Remove from database
          const updatedVideos = contentItems?.videos
            ?.filter(item => item?.id !== itemId)
            ?.map(v => ({
              title: v?.title,
              url: v?.url,
              uploadedDate: v?.uploadedDate
            }));

          await boothService?.updateVideoUrls(instituteId, updatedVideos);

          // Update local state
          setContentItems(prev => ({
            ...prev,
            videos: prev?.videos?.filter(item => item?.id !== itemId)
          }));

          alert('Video deleted successfully!');
        } catch (error) {
          console.error('Error deleting video:', error);
          alert('Failed to delete video. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        // For brochures (local state only)
        setContentItems(prev => ({
          ...prev,
          [activeTab]: prev?.[activeTab]?.filter(item => item?.id !== itemId)
        }));
      }
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!instituteId) {
      alert('Institute profile not found. Please try again.');
      return;
    }

    // Validate file using utility
    const validation = validateImageFile(file, 'logo');
    if (!validation?.valid) {
      alert(validation?.error);
      return;
    }

    try {
      setLogoUploading(true);

      // Upload to storage
      const { publicUrl } = await storageService?.uploadBoothImage(file, instituteId, 'logo');

      // Update institute profile
      await storageService?.updateInstituteImages(instituteId, { logoUrl: publicUrl });

      // Update local state
      setLogoUrl(publicUrl);
      alert('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!instituteId) {
      alert('Institute profile not found.');
      return;
    }

    if (!confirm('Are you sure you want to delete your logo?')) {
      return;
    }

    try {
      setLoading(true);

      // Update institute profile to remove logo
      await storageService?.updateInstituteImages(instituteId, { logoUrl: null });

      // Update local state
      setLogoUrl(null);
      alert('Logo deleted successfully!');
    } catch (error) {
      console.error('Error deleting logo:', error);
      alert('Failed to delete logo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'pdf': return 'FileText';
      default: return 'File';
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadType('file');
    setVideoUrl('');
    setLinkTitle('');
    setLinkUrl('');
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border">
        <div className="flex items-center justify-between p-4 md:p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FolderOpen" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Booth Content
              </h2>
              <p className="text-sm text-muted-foreground">Manage your virtual booth materials</p>
            </div>
          </div>
        </div>

        <div className="flex overflow-x-auto px-4 md:px-6">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-smooth whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="font-medium">{tab?.label}</span>
              <span className="px-2 py-0.5 bg-muted rounded-full text-xs font-semibold data-text">
                {tab?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 md:p-6">
        {activeTab !== 'logo' && activeTab !== 'campusTours' && (
          <div className="mb-4 md:mb-6">
            <Button
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={openUploadModal}
              className="w-full md:w-auto"
            >
              Upload {activeTab === 'videos' ? 'Video' : activeTab === 'brochures' ? 'Brochure' : 'Add Link'}
            </Button>
          </div>
        )}

        {Object.keys(uploadProgress)?.length > 0 && (
          <div className="mb-4 md:mb-6 space-y-3">
            {Object.entries(uploadProgress)?.map(([id, progress]) => (
              <div key={id} className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Uploading...</span>
                  <span className="text-sm font-semibold text-primary data-text">{progress}%</span>
                </div>
                <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          {activeTab === 'logo' && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Booth Logo</h3>
                
                {/* Size Requirements Notice */}
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <strong>Recommended Size:</strong> 250×100px (2.5:1 ratio)<br />
                      <strong>Max File Size:</strong> 100KB<br />
                      <strong>Formats:</strong> PNG (transparent), JPG, WebP
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Upload your institute logo to display on your virtual booth.
                </p>
                
                {logoUrl ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center bg-background rounded-lg p-8 border-2 border-border">
                      <img 
                        src={logoUrl} 
                        alt="Institute Logo" 
                        className="max-w-full max-h-48 object-contain"
                      />
                    </div>
                    <div className="flex gap-3">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/jpg"
                          onChange={handleLogoUpload}
                          disabled={logoUploading || loading}
                          className="hidden"
                        />
                        <Button
                          variant="default"
                          iconName="Upload"
                          iconPosition="left"
                          disabled={logoUploading || loading}
                          className="w-full"
                          onClick={(e) => e?.currentTarget?.previousElementSibling?.click()}
                        >
                          {logoUploading ? 'Uploading...' : 'Replace Logo'}
                        </Button>
                      </label>
                      <Button
                        variant="ghost"
                        iconName="Trash2"
                        iconPosition="left"
                        onClick={handleLogoDelete}
                        disabled={logoUploading || loading}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/jpg"
                        onChange={handleLogoUpload}
                        disabled={logoUploading || loading}
                        className="hidden"
                      />
                      <div className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-smooth">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="Upload" size={32} color="var(--color-primary)" />
                          </div>
                          <div>
                            <p className="text-foreground font-medium mb-1">
                              {logoUploading ? 'Uploading...' : 'Click to upload logo'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {getImageUploadNotice('logo')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'videos' && contentItems?.videos?.map((item) => (
            <div key={item?.id} className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-smooth">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={getContentIcon(item?.type)} size={24} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground truncate">{item?.title}</h4>
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span className="data-text">{item?.size}</span>
                  <span>•</span>
                  <span>{item?.uploadedDate}</span>
                  <span>•</span>
                  <span className="data-text">{item?.views} views</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => handleEditVideo(item)}>
                  <Icon name="Edit" size={18} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item?.id)}>
                  <Icon name="Trash2" size={18} />
                </Button>
              </div>
            </div>
          ))}

          {activeTab === 'brochures' && contentItems?.brochures?.map((item) => (
            <div key={item?.id} className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-smooth">
              <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name={getContentIcon(item?.type)} size={24} color="var(--color-accent)" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground truncate">{item?.title}</h4>
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span className="data-text">{item?.size}</span>
                  <span>•</span>
                  <span>{item?.uploadedDate}</span>
                  <span>•</span>
                  <span className="data-text">{item?.downloads} downloads</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon">
                  <Icon name="Download" size={18} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item?.id)}>
                  <Icon name="Trash2" size={18} />
                </Button>
              </div>
            </div>
          ))}

          {activeTab === 'links' && contentItems?.links?.map((item) => (
            <div key={item?.id} className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-smooth">
              <div className="flex-shrink-0 w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="Link" size={24} color="var(--color-secondary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground truncate">{item?.title}</h4>
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span className="truncate">{item?.url}</span>
                  <span>•</span>
                  <span className="data-text">{item?.clicks} clicks</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon">
                  <Icon name="ExternalLink" size={18} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item?.id)}>
                  <Icon name="Trash2" size={18} />
                </Button>
              </div>
            </div>
          ))}

          {activeTab === 'campusTours' && (
            <>
              <div className="mb-4 md:mb-6">
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => {
                    // Clear inputs when opening add form
                    setTourTitle('');
                    setTourUrl('');
                  }}
                  className="w-full md:w-auto"
                >
                  Add 360 Campus Tour
                </Button>
              </div>

              {contentItems?.campusTours?.length > 0 && (
                <div className="space-y-3 mb-6">
                  <h4 className="text-sm font-semibold text-foreground">Existing Campus Tours</h4>
                  {contentItems?.campusTours?.map((item) => (
                    <div key={item?.id} className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-smooth">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Compass" size={24} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground truncate">{item?.title}</h4>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          <span className="truncate">{item?.url}</span>
                          <span>•</span>
                          <span>{item?.uploadedDate}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => window.open(item?.url, '_blank')}>
                          <Icon name="ExternalLink" size={18} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item?.id)}>
                          <Icon name="Trash2" size={18} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-4">Add New 360 Campus Tour</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Tour Location Name
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., Main Campus, Library, Sports Complex"
                      value={tourTitle}
                      onChange={(e) => setTourTitle(e?.target?.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTourUploadType('url')}
                      className={`flex-1 py-2 px-4 rounded-lg border transition-smooth ${
                        tourUploadType === 'url' ?'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary'
                      }`}
                    >
                      <Icon name="Link" size={18} className="inline mr-2" />
                      URL
                    </button>
                    <button
                      onClick={() => setTourUploadType('file')}
                      className={`flex-1 py-2 px-4 rounded-lg border transition-smooth ${
                        tourUploadType === 'file' ?'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary'
                      }`}
                    >
                      <Icon name="Upload" size={18} className="inline mr-2" />
                      File
                    </button>
                  </div>
                  {tourUploadType === 'url' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Panoramic Image URL
                      </label>
                      <Input
                        type="url"
                        placeholder="https://example.com/panorama.jpg"
                        value={tourUrl}
                        onChange={(e) => setTourUrl(e?.target?.value)}
                      />
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-blue-800">
                            <strong>360° Image Requirements:</strong> Equirectangular format, 4096×2048px (2:1 ratio) | Max 2MB | JPG/PNG
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {tourUploadType === 'file' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Panoramic Image File
                      </label>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/jpg"
                        onChange={(e) => setTourImageFile(e?.target?.files?.[0])}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white file:cursor-pointer hover:file:bg-primary/90"
                      />
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-blue-800">
                            <strong>360° Image Requirements:</strong> Equirectangular format, 4096×2048px (2:1 ratio) | Max 2MB | JPG/PNG
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <Button
                    fullWidth
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleCampusTourSubmit}
                    disabled={loading || !tourTitle?.trim() || !tourUrl?.trim()}
                  >
                    {loading ? 'Adding...' : 'Add 360 Tour'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">
                {editingVideo ? 'Edit Video' : activeTab === 'videos' ? 'Upload Video' : activeTab === 'brochures' ? 'Upload Brochure' : 'Add Link'}
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setEditingVideo(null);
                  setVideoUrl('');
                  setVideoTitle('');
                }}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {activeTab === 'videos' && (
              <div className="space-y-4">
                {!editingVideo && (
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setUploadType('file')}
                      className={`flex-1 py-2 px-4 rounded-lg border transition-smooth ${
                        uploadType === 'file' ?'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary'
                      }`}
                    >
                      <Icon name="Upload" size={18} className="inline mr-2" />
                      Upload File
                    </button>
                    <button
                      onClick={() => setUploadType('url')}
                      className={`flex-1 py-2 px-4 rounded-lg border transition-smooth ${
                        uploadType === 'url' ?'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary'
                      }`}
                    >
                      <Icon name="Link" size={18} className="inline mr-2" />
                      Video URL
                    </button>
                  </div>
                )}

                {uploadType === 'file' && !editingVideo ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Choose Video File
                    </label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleFileUpload}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white file:cursor-pointer hover:file:bg-primary/90"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported formats: MP4, AVI, MOV, WMV (Max 500MB)
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Video Title
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter video title"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e?.target?.value)}
                      className="mb-4"
                    />
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Video URL
                    </label>
                    <Input
                      type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e?.target?.value)}
                      className="mb-4"
                    />
                    <p className="text-xs text-muted-foreground mb-4">
                      Paste a YouTube, Vimeo, or direct video URL
                    </p>
                    <Button
                      variant="default"
                      onClick={handleVideoUrlSubmit}
                      disabled={!videoUrl?.trim()}
                      className="w-full"
                    >
                      {editingVideo ? 'Update Video' : 'Add Video URL'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'brochures' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Choose PDF/Brochure
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white file:cursor-pointer hover:file:bg-primary/90"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Supported formats: PDF, DOC, DOCX (Max 50MB)
                </p>
              </div>
            )}

            {activeTab === 'links' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Link Title
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Online Application Portal"
                    value={linkTitle}
                    onChange={(e) => setLinkTitle(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    URL
                  </label>
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e?.target?.value)}
                  />
                </div>
                <Button
                  variant="default"
                  onClick={handleLinkSubmit}
                  disabled={!linkTitle?.trim() || !linkUrl?.trim() || loading}
                  className="w-full"
                >
                  {loading ? 'Adding...' : 'Add Link'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoothContentManager;